import {Button, Select, ToastProvider} from '@perion-undertone/ut-react-common';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import isValidDomain from 'is-valid-domain';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {textProvider} from '../imports';
import {PerionLoader} from '../actionImports';
import {
    ALLOWED_FILE_ACCEPT,
    PARSE_ERROR_TYPES,
    parseDomainsFromFile,
    validateParsedDomains
} from './domainFileParser';

const CONSTANT_TEXTS = {
    FILE_READING_FAILED: textProvider.getText('supplySourceSettingsModal', 'readingFilefaild'),
    FILE_READING_ABORTED: textProvider.getText('supplySourceSettingsModal', 'readingFileAborted'),
    Browse: textProvider.getText('supplySourceSettingsModal', 'browse'),
    dragFile: textProvider.getText('supplySourceSettingsModal', 'dragFile'),
    invalidFiles: textProvider.getText('supplySourceSettingsModal', 'invalidFiles'),
    singleInvalidFile: textProvider.getText('supplySourceSettingsModal', 'singleInvalidFile'),
    unsupportedFile: textProvider.getText('supplySourceSettingsModal', 'unsupportedFile'),
    noDomainsInFile: textProvider.getText('supplySourceSettingsModal', 'noDomainsInFile'),
    noValidDomainsInFile: textProvider.getText('supplySourceSettingsModal', 'noValidDomainsInFile'),
    couldNotParseFile: textProvider.getText('supplySourceSettingsModal', 'couldNotParseFile'),
    loadingFile: textProvider.getText('common', 'loading'),
};

const getParseErrorMessage = (error) => {
    if (error.message === 'aborted') {
        return CONSTANT_TEXTS.FILE_READING_ABORTED;
    }

    if (error.message === 'failed') {
        return CONSTANT_TEXTS.FILE_READING_FAILED;
    }

    if (error.message === PARSE_ERROR_TYPES.NO_DOMAINS) {
        return CONSTANT_TEXTS.noDomainsInFile;
    }

    return CONSTANT_TEXTS.couldNotParseFile;
};

export default function DomainsDropZone(props) {
    const [domains, setDomains] = useState([]);
    const [invalidDomains, setInvalidDomains] = useState([]);
    const [fileError, setFileError] = useState(null);
    const [isParsingFile, setIsParsingFile] = useState(false);
    const domainsRefreshRequests = useMemo(() => props.domainsRefresh, []);

    const applyDomainValidation = useCallback((nextDomains) => {
        const tempInValidDomains = [];
        const tempValidDomains = [];

        if (Array.isArray(nextDomains)) {
            if (nextDomains.length === 0) {
                setInvalidDomains([]);
                setDomains([]);
                setFileError(null);
                return;
            }

            for (const domain of nextDomains) {
                if (!isValidDomain(domain.value)) {
                    tempInValidDomains.push(domain);
                } else {
                    domain.value = _.toLower(domain.value);
                    domain.label = domain.value;
                    tempValidDomains.push(domain);
                }
            }

            setInvalidDomains(tempInValidDomains);
            setDomains(tempValidDomains);
            setFileError(null);
        } else {
            setDomains(nextDomains);
        }
    }, []);

    const handleDomainsChange = useCallback((nextDomains) => {
        applyDomainValidation(nextDomains);
    }, [applyDomainValidation]);

    const onDropAccepted = useCallback(async (acceptedFiles) => {
        const file = acceptedFiles[0];

        if (!file) {
            return;
        }

        setIsParsingFile(true);
        setFileError(null);
        setDomains([]);
        setInvalidDomains([]);

        try {
            const rawValues = await parseDomainsFromFile(file);
            const {validDomains, invalidDomains: invalidParsedDomains} = validateParsedDomains(rawValues);

            setInvalidDomains(invalidParsedDomains);
            setDomains(validDomains);
            setFileError(validDomains.length === 0 ? CONSTANT_TEXTS.noValidDomainsInFile : null);
        } catch (error) {
            setDomains([]);
            setInvalidDomains([]);
            setFileError(getParseErrorMessage(error));
        } finally {
            setIsParsingFile(false);
        }
    }, []);

    const onDropRejected = useCallback(() => {
        ToastProvider.error(CONSTANT_TEXTS.unsupportedFile);
    }, []);

    const {getRootProps, getInputProps} = useDropzone({
        onDrop: onDropAccepted,
        onDropRejected,
        accept: ALLOWED_FILE_ACCEPT,
        multiple: false,
        disabled: isParsingFile,
    });

    useEffect(() => {
        if (domainsRefreshRequests != props.domainsRefresh) {
            setDomains([]);
            setInvalidDomains([]);
            setFileError(null);
            setIsParsingFile(false);
        }
    }, [props.domainsRefresh, domainsRefreshRequests]);

    if (isParsingFile) {
        return (
            <section className="domain-drop-container domain-drop-container--loading">
                <PerionLoader size="small" overlay={false} />
                <span className="domain-drop-loading-text">{CONSTANT_TEXTS.loadingFile}</span>
            </section>
        );
    }

    const invalidDomainsMessage = invalidDomains.length >= 1 && (
        <div className='invalid-domain-error'>
            *{_.get(_.first(invalidDomains), 'value')} {invalidDomains.length > 1 ? `+${invalidDomains.length - 1} ${CONSTANT_TEXTS.invalidFiles}` : ` ${CONSTANT_TEXTS.singleInvalidFile}`}
        </div>
    );

    if (domains && domains.length > 0) {
        return (
            <>
                <div className='selected-domains-dropdown'>
                    <Select creatable
                        isMulti
                        name="domains"
                        hookId="domains-select-dropzone"
                        value={domains}
                        openMenuOnFocus={false}
                        openMenuOnClick={false}
                        styles={props.getCustomStyles}
                        onChange={handleDomainsChange}
                        formatCreateLabel={props.formatCreateLabel}
                        type="domain"
                    />
                    <Button
                        disabled={_.get(domains, 'length', 0) < 1}
                        hookId='handle-block-domains-btn'
                        onClick={() => props.setBlockDomains(domains)}
                    >
                        {props.blockBtnText}
                    </Button>
                </div>
                {invalidDomainsMessage}
            </>
        );
    }

    return (
        <section className="domain-drop-container">
            <div {...getRootProps({className: 'dropzone'})}>
                <input {...getInputProps()} />
                <p>{CONSTANT_TEXTS.dragFile}  <Button icon="icon-search" className='dropzone-button' hookId='dropzone-button'>{CONSTANT_TEXTS.Browse}</Button>
                </p>
            </div>
            {fileError && <div className='invalid-domain-error'>{fileError}</div>}
            {invalidDomainsMessage}
        </section>
    );
}

DomainsDropZone.propTypes = {
    domainsRefresh: PropTypes.number,
    blockBtnText: PropTypes.string,
    getCustomStyles: PropTypes.object,
    formatCreateLabel: PropTypes.func,
    setBlockDomains: PropTypes.func
};
